import BaseExecutor from 'services/sports/default/base-executor';
import {calcUserScore, filterObjectByParams} from 'helpers/common-helper';
import {calcSympathyPoints, getDefaultExposure, createCsvPlayer, getPlayerNameSlug} from 'helpers/player-helper';
import {PlayersPoolBuilder} from 'services/players-pool/players-pool-builder';
import Action from 'actions';
import NumberHelper, {toFloat} from 'helpers/number-helper';
import Const from 'constants';
import {addRoleSuffix} from 'helpers/simplex-helper';
import {SearchRuleBuilder as RuleBuilder} from 'services/search-rule-builder';
import {calcRangeOfOutcomesValue, getRandomisationValuePtc} from 'components/organisms/range-of-outcomes/helpers';
import {calcBoostedPoints} from 'components/organisms/projection-boost-rule/helpers';

export default class BasePlayer extends BaseExecutor {
    getExpandedEntityParams({incomingValues: {player}}) {
        const positionShortNames = player?.positionName.split('/') ?? [];

        return {
            ...player,
            nameSlug: getPlayerNameSlug(player.name),
            mode: Const.DEFAULT_MODE,
            positionShortNames,
            userScoreMultiplier: 1000,
            score: parseFloat(player.score),
            points: parseFloat(player.points),
            adminPoints: parseFloat(player.adminPoints),
            exposureMin: getDefaultExposure('min'),
            exposureMax: getDefaultExposure('max'),
            pointsChangedManually: false,
            projectedOwnershipChangedManually: false,
            getId() {
                return this.playerId;
            },
            setMode(mode) {
                this.mode = mode;
            },
            getMode() {
                return this.mode;
            },
            setModePropValue(mode, prop, value) {
                let propName = this.getPropNameOfMode(prop, mode);
                this.setPropValue(propName, value);
            },
            setPropValue(prop, value) {
                this[prop] = value;
            },
            getPropNameOfMode: (prop, incomingMode) => prop,
            getPropValueOfMode(prop, incomingMode) {
                let value,
                    propName = this.getPropNameOfMode(prop, incomingMode);

                if (this.hasOwnProperty(propName)) {
                    value = this[propName];
                }

                return value;
            },
            getPositionNames() {
                return this.positionShortNames;
            },
            isModeUsed: (mode) => true,
            isExcludedByFilter: () => false,
            isExcludedBy(param) {
                return this[param];
            },
            excludeBy(arr = [], exclude = true) {
                arr.map((item) => {
                    this[item] = exclude;
                });
            },
            include() {
                this.includeBy([]);
            },
            includeBy(arr = []) {
                this.excludeBy(arr, false);
            },
            resetExposure() {
                this.exposureMin = getDefaultExposure('min');
                this.exposureMax = getDefaultExposure('max');
            },
            getSalaryByMode() {
                return this.getPropValueOfMode('salary');
            },
            getUserPointsByMode() {
                return Math.round(this.getPropValueOfMode('userPoints') * 1000) / 1000;
            },
            getCeilingPointsByMode() {
                return this.getPropValueOfMode('ceilingPoints');
            },
            getFptsPerSalary() {
                if (parseInt(this.salary)) {
                    return toFloat((parseFloat(this.userPoints) / parseInt(this.salary)) * 1000, 3);
                }

                return 0;
            },
            // #20606. This function has been added because of difference
            // between attributes game and (team, opponent)
            getGameKey() {
                return [this.team, this.opponent].sort().join('');
            },
            isLockedInMode(mode) {
                const {playersLock} = Action.getState();

                return playersLock.isLocked(this.getId(), mode);
            },
            isExcludedAnywhere(params = {}) {
                const {
                    checkUserExclusion = true,
                    checkPointsExclusion = true,
                    checkSalaryExclusion = true,
                    checkFilterExclusion = true,
                } = params;

                const {playersExclusion, modesConfig} = Action.getState();

                if (checkUserExclusion && playersExclusion.isExcludedAnywhere(this.getId())) {
                    return true;
                }

                const modes = modesConfig.all({asArr: true});
                for (let mode of modes) {
                    if (checkPointsExclusion && this.isExcludedByPoints(mode.getRole())) {
                        return true;
                    }
                    if (checkSalaryExclusion && this.isExcludedBySalary(mode.getRole())) {
                        return true;
                    }
                }

                return checkFilterExclusion && this.isExcludedByFilter();
            },
            isExcludedByMode(mode, params = {}) {
                const {
                    checkUserExclusion = true,
                    checkPointsExclusion = true,
                    checkSalaryExclusion = true,
                    checkFilterExclusion = true,
                    salaryMode = mode,
                } = params;

                if (checkUserExclusion && this.isExcludedByUser(mode)) {
                    return true;
                }

                if (checkPointsExclusion && this.isExcludedByPoints(mode)) {
                    return true;
                }

                if (checkSalaryExclusion && this.isExcludedBySalary(salaryMode)) {
                    return true;
                }

                return checkFilterExclusion && this.isExcludedByFilter();
            },
            isExcludedByUser(mode) {
                const {playersExclusion} = Action.getState();

                return playersExclusion.isExcluded(this.getId(), mode);
            },
            isExcludedByPoints(mode) {
                const {pointsBoundary} = Action.getState();
                const points = this.getPropValueOfMode('userPoints', mode);

                return !pointsBoundary.isInBoundaries(points, mode);
            },
            isExcludedBySalary(mode) {
                const {salaryBoundary} = Action.getState();
                const salary = this.getPropValueOfMode('salary', mode);

                return !salaryBoundary.isInBoundaries(salary, mode);
            },
            isDuplicate(lineupPlayerIds) {
                return lineupPlayerIds.some((playerId) => this.getId() === playerId);
            },
            isFitForLineup(substitutablePlayer) {
                if (
                    [Const.CAPTAIN_MODE, Const.STAR_MODE, Const.PRO_MODE].includes(substitutablePlayer.positionInLineup)
                ) {
                    return true;
                }
                const substitutablePlayerLineupPositions = substitutablePlayer.positionInLineup.split('/');
                const substitutePlayerLineupPositions = this.positionName.split('/');

                return substitutePlayerLineupPositions.some((position) =>
                    substitutablePlayerLineupPositions.includes(position),
                );
            },
            getPropValue(propName, defaultValue = '') {
                if (propName in this) {
                    return this[propName];
                }

                return defaultValue;
            },
            getPropValueOfLineupRole(prop) {
                if (prop === 'projected_ownership' && 'projOwn' in this) {
                    return this.projOwn.getValue();
                }

                return this[prop];
            },
            isUserPointsGreaterThan(params) {
                const {points, userPointsType, mode} = params;
                const value = this.getPropValueOfMode(userPointsType, mode);

                return value > points;
            },
            isUserPointsLessThan(params) {
                const {points, userPointsType, mode} = params;
                const value = this.getPropValueOfMode(userPointsType, mode);

                return value < points;
            },
        };
    }

    annulPlayerProjections({incomingValues: {player}}) {
        player.score = 0;
        player.userScore = 0;
        player.points = 0;
        player.adminPoints = 0;
        player.userPoints = 0;

        return player;
    }

    createPoolsOfPlayers({incomingValues: {players}}) {
        const {playersLock} = Action.getState();
        const builder = new PlayersPoolBuilder(players);

        builder.addTaskOnScenario('position');
        builder.addTaskOnScenario('playerId');
        builder.addTaskOnScenario('teamAndPosition');
        builder.addTaskOnScenario('allowedPositions');
        builder.addTaskOnScenario('externalId');
        builder.addTaskOnProperty('team');
        builder.addTaskOnProperty('exposureMin', function (player) {
            return !playersLock.isLockedAnywhere(player.getId()) && player.exposureMin > 0;
        });
        builder.addTaskOnProperty('exposureMax', function (player) {
            return !playersLock.isLockedAnywhere(player.getId()) && player.exposureMax < 100;
        });

        return builder.execTasks();
    }

    createPoolsOfPlayers_ShowDown({incomingValues: {players}}) {
        const {playersLock} = Action.getState();
        const builder = new PlayersPoolBuilder(players);

        builder.addTaskOnScenario('position');
        builder.addTaskOnScenario('playerId');
        builder.addTaskOnScenario('teamAndPosition');
        builder.addTaskOnProperty('team');
        builder.addTaskOnProperty('exposureMin', function (player) {
            return (
                !player.isExcludedByPoints(Const.FLEX_MODE) &&
                !playersLock.isLockedAnywhere(player.getId()) &&
                player.exposureMin > 0
            );
        });
        builder.addTaskOnProperty('exposureMax', function (player) {
            return (
                !player.isExcludedByPoints(Const.FLEX_MODE) &&
                !playersLock.isLockedAnywhere(player.getId()) &&
                player.exposureMax < 100
            );
        });
        builder.addTaskOnProperty('captain_exposureMin', function (player) {
            return (
                !player.isExcludedByPoints(Const.CAPTAIN_MODE) &&
                !playersLock.isLockedAnywhere(player.getId()) &&
                player.captain_exposureMin > 0
            );
        });
        builder.addTaskOnProperty('captain_exposureMax', function (player) {
            return (
                !player.isExcludedByPoints(Const.CAPTAIN_MODE) &&
                !playersLock.isLockedAnywhere(player.getId()) &&
                player.captain_exposureMax < 100
            );
        });
        builder.addTaskOnProperty('overall_exposureMax', function (player) {
            return !playersLock.isLockedAnywhere(player.getId()) && player.overall_exposureMax < 100;
        });

        return builder.execTasks();
    }

    preparePoolsOfPlayers({incomingValues: {players, filterPlayersBeforePreparingPools}}) {
        if (!filterPlayersBeforePreparingPools) {
            return this.exec('createPoolsOfPlayers', {
                players: Object.values(players),
            });
        }

        const {
            sportConfig,
            playersData: {games, filters},
        } = Action.getState();

        const params = {
            ...RuleBuilder.callback('playerId', function (player) {
                if (!player.isExcludedAnywhere()) {
                    return true;
                }

                return sportConfig.isModeLateswap() && games.isPlayerGameLocked(player);
            }),
            team: {
                filterType: Const.FILTER_TYPE_IN_ARRAY,
                filterValue: filters.games,
            },
        };

        players = filterObjectByParams(players, params, true);

        return this.exec('createPoolsOfPlayers', {
            players,
        });
    }

    prepareRandomPoints() {
        const {rangeOfOutcomes, sportConfig, lineupsQuantity, playersData, projectionPointsType} = Action.getState();
        const rangeOfOutcomesPct = getRandomisationValuePtc(rangeOfOutcomes, sportConfig, lineupsQuantity);

        if (0 >= rangeOfOutcomesPct) {
            return {};
        }

        const {preparedPlayers} = playersData;
        const players = preparedPlayers.groupedBy.playerId();
        const propName = projectionPointsType.getProjectionPointsType();

        const playerPoints = {};
        Object.values(players).map((player) => {
            const points = NumberHelper.float(player[propName], 3);

            playerPoints[player.getId()] = calcRangeOfOutcomesValue(points, rangeOfOutcomesPct);
        });

        return {...playerPoints};
    }

    prepareRandomPointsByModes({incomingValues = {}}) {
        const {rangeOfOutcomes, sportConfig, lineupsQuantity, projectionPointsType, playersData} = Action.getState();
        const rangeOfOutcomesPct = getRandomisationValuePtc(rangeOfOutcomes, sportConfig, lineupsQuantity);

        if (0 >= rangeOfOutcomesPct) {
            return {};
        }

        const {preparedPlayers} = playersData;
        const players = preparedPlayers.groupedBy.playerId();
        const {modes = [Const.CAPTAIN_MODE, Const.DEFAULT_MODE]} = incomingValues;
        const prop = projectionPointsType.getProjectionPointsType();

        const projPointsTypesIndexedByMode = {};
        modes.map((mode) => {
            projPointsTypesIndexedByMode[mode] = this.exec('definePropNameOfMode', {prop, mode});
        });

        const playerPoints = {};
        Object.values(players).map((player) => {
            Object.keys(projPointsTypesIndexedByMode).map((mode) => {
                const propName = projPointsTypesIndexedByMode[mode];
                const points = NumberHelper.float(player[propName], 3);
                const key = addRoleSuffix({prefix: player.getId(), role: mode});

                playerPoints[key] = calcRangeOfOutcomesValue(points, rangeOfOutcomesPct);
            });
        });

        return {...playerPoints};
    }

    preparePlayersForLineup({incomingValues = {}}) {
        const {lineupPlayers, lineupNumber, projectionBoostMap, projectionPointsType} = incomingValues;

        return lineupPlayers.map((player) => {
            return this.exec('preparePlayerForLineup', {
                player,
                lineupNumber,
                projectionBoostMap,
                projectionPointsType,
            });
        });
    }

    preparePlayerForLineup({incomingValues = {}}) {
        const {
            sportConfig,
            randomPoints,
        } = Action.getState();
        const {player, lineupNumber, projectionBoostMap, projectionPointsType} = incomingValues;

        const result = cp(player);

        result.positionName = sportConfig.getPositionName(result.position, result.positionName);
        result.positionSynonym = sportConfig.getPositionSynonym(result.position, result.positionName);
        result.lineupPoints = randomPoints.items?.[lineupNumber]?.[result.playerId] ?? result.userPoints;
        result.boostedPoints = 0;

        const boostStatement = projectionBoostMap[result.playerId];

        if (boostStatement) {
            result.boostedPoints = calcBoostedPoints(result[projectionPointsType], boostStatement);
            result.lineupPoints += result.boostedPoints;
        }

        return {...result};
    }

    preparePlayerForLineup_ShowDown({incomingValues = {}}) {
        const {
            sportConfig,
            randomPoints,
        } = Action.getState();
        const {player, lineupNumber, projectionBoostMap, projectionPointsType} = incomingValues;

        const result = cp(player);

        result.positionName = sportConfig.getPositionName(result.position, result.positionName);
        result.positionSynonym = sportConfig.getPositionSynonym(result.position, result.positionName);
        result.lineupPoints = result.getPropValueOfLineupRole('userPoints');
        result.boostedPoints = 0;

        if (randomPoints.items.hasOwnProperty(lineupNumber)) {
            const key = addRoleSuffix({
                prefix: result.playerId,
                role: result.getLineupRole(),
            });
            if (randomPoints.items[lineupNumber].hasOwnProperty(key)) {
                result.lineupPoints = randomPoints.items[lineupNumber][key];
            }
        }

        const boostStatement = projectionBoostMap[result.playerId];

        if (boostStatement) {
            const points = result.getPropValueOfLineupRole(projectionPointsType);
            result.boostedPoints = calcBoostedPoints(points, boostStatement);
            result.lineupPoints += result.boostedPoints;
        }

        return {...result};
    }

    afterGettingPlayers({incomingValues: {dispatch}}) {
        dispatch(Action.do('unsetTeamDetails'));
    }

    resetPoints({incomingValues: {dispatch}}) {
        const {
            playersData: {players},
            userPointsType,
        } = Action.getState();

        const userPoints = [];

        players.getAllAsArr().map((player) => {
            userPoints.push({
                playerId: player.playerId,
                userPoints: this.exec('applySympathyToPoints', {
                    player,
                    points: player[userPointsType.getUserPointsType()],
                }),
            });
        });

        return dispatch(Action.set('UserPoints', userPoints, Const.FLEX_MODE, false));
    }

    resetPoints_ShowDown({incomingValues: {dispatch, modes = [Const.CAPTAIN_MODE, Const.FLEX_MODE]}}) {
        const {
            playersData: {players},
            userPointsType,
        } = Action.getState();

        const userPointsByModes = {};
        players.getAllAsArr().map((player) => {
            modes.map((mode) => {
                userPointsByModes[mode] ??= [];
                userPointsByModes[mode].push({
                    playerId: player.getId(),
                    userPoints: this.exec('applySympathyToPoints', {
                        player,
                        points: player.getPropValueOfMode(userPointsType.getUserPointsType(), mode),
                        mode,
                    }),
                });
            });
        });

        modes.map((mode) => {
            dispatch(Action.set('UserPoints', userPointsByModes[mode], mode, false));
        });

        return true;
    }

    resetSettings({incomingValues: {dispatch}}) {
        const {
            playersData: {players},
            userPointsType,
        } = Action.getState();

        const playersUserPoints = [];
        const playersExposure = [];

        players.getAllAsArr().map((player) => {
            playersUserPoints.push({
                playerId: player.playerId,
                name: player.name,
                userPoints: this.exec('applySympathyToPoints', {
                    player,
                    points: player[userPointsType.getUserPointsType()],
                }),
            });

            playersExposure.push({
                playerId: player.playerId,
                exposureMin: getDefaultExposure('min'),
                exposureMax: getDefaultExposure('max'),
            });
        });

        dispatch(Action.do('resetExcludePlayersByRange'));
        dispatch(Action.set('PlayersExposure', playersExposure));
        dispatch(Action.set('UserPoints', playersUserPoints, Const.FLEX_MODE, false, {countPlayers: false}));

        return true;
    }

    resetSettings_ShowDown({incomingValues: {dispatch, modes = [Const.CAPTAIN_MODE, Const.FLEX_MODE]}}) {
        const {
            playersData: {players},
            userPointsType,
        } = Action.getState();

        const userPointsByModes = {};
        const exposure = [];
        players.getAllAsArr().map((player) => {
            modes.map((mode) => {
                userPointsByModes[mode] ??= [];
                userPointsByModes[mode].push({
                    playerId: player.getId(),
                    userPoints: this.exec('applySympathyToPoints', {
                        player,
                        points: player.getPropValueOfMode(userPointsType.getUserPointsType(), mode),
                        mode,
                    }),
                });

                exposure.push({
                    playerId: player.getId(),
                    exposureMin: getDefaultExposure('min'),
                    exposureMax: getDefaultExposure('max'),
                    mode,
                });
            });
        });

        dispatch(Action.do('resetExcludePlayersByRange'));
        dispatch(Action.set('PlayersExposure', exposure));

        modes.map((mode) => {
            dispatch(Action.set('UserPoints', userPointsByModes[mode], mode, false, {countPlayers: false}));
        });

        return true;
    }

    preparePlayersDataForDownloading({incomingValues: {players}}) {
        return players.map((player) => this.exec('preparePlayerDataForDownloading', {player}));
    }

    preparePlayerDataForDownloading({incomingValues: {player}}) {
        return createCsvPlayer(player.externalId, player);
    }

    preparePlayerDataForDownloading_ShowDown({incomingValues: {player}}) {
        const externalId =
            player.getIsCaptain() && player.hasOwnProperty('externalCaptainId')
                ? player.externalCaptainId
                : player.externalId;

        return createCsvPlayer(externalId, player);
    }

    applySympathyToPoints({incomingValues: {player, points, mode = Const.DEFAULT_MODE}}) {
        const {playersSympathy} = Action.getState();
        const sympathy = playersSympathy.getByPlayerIdAndMode(player.getId(), mode);
        const result = calcSympathyPoints(points, Const.SYMPATHIES_LIST[sympathy]);

        return toFloat(result, 3);
    }

    updatePlayersAfterRefreshingProjections({incomingValues: {indexedPlayers}}) {
        const {
            playersData: {players},
            playersSympathy,
        } = Action.getState();

        const result = {};

        Object.keys(indexedPlayers).map((key) => {
            const newPlayer = indexedPlayers[key];
            const player = {...players.getPlayer(newPlayer.getId())};

            if (isEmpty(player)) {
                result[key] = {...newPlayer};
                return;
            }

            let userPoints = player.userPoints;
            let userScore = player.userScore;

            if (!player.pointsChangedManually) {
                const sympathy = playersSympathy.getByPlayerIdAndMode(player.getId());
                userPoints = NumberHelper.float(
                    calcSympathyPoints(newPlayer.userPoints, Const.SYMPATHIES_LIST[sympathy]),
                    3,
                );
                userScore = calcUserScore(userPoints, newPlayer.salary, newPlayer.userScoreMultiplier);
            }

            let positionName = newPlayer.positionName;
            let position = newPlayer.position;

            const isPositionPresent =
                newPlayer.hasOwnProperty('selectablePositionsMap') &&
                newPlayer.selectablePositionsMap.hasOwnProperty(player.positionName);

            if (isPositionPresent) {
                positionName = player.positionName;
                position = player.position;
            }

            const {projOwn = {}, ...newPlayerProps} = newPlayer;

            if ('projOwn' in player) {
                player.projOwn.setValueFromPlayerPayload(newPlayer);
            }

            result[key] = {
                ...player,
                ...newPlayerProps,
                pointsChangedManually: player.pointsChangedManually,
                userPoints,
                userScore,
                grades: newPlayer.grades,
                exposureMax: player.exposureMax,
                exposureMin: player.exposureMin,
                positionName,
                position,
            };
        });

        return {...result};
    }

    updatePlayersAfterRefreshingProjections_ShowDown({incomingValues: {indexedPlayers}}) {
        const {
            playersData: {players},
            playersSympathy,
        } = Action.getState();

        const result = {};

        Object.keys(indexedPlayers).map((key) => {
            const newPlayer = indexedPlayers[key];
            const player = {...players.getPlayer(newPlayer.getId())};

            if (isEmpty(player)) {
                result[key] = {...newPlayer};
                return;
            }

            let userPoints = player.userPoints;
            let userScore = player.userScore;
            let captain_userPoints = player.captain_userPoints;
            let captain_userScore = player.captain_userScore;

            if (!player.pointsChangedManually) {
                const sympathy = playersSympathy.getByPlayerIdAndMode(player.getId());
                userPoints = NumberHelper.float(
                    calcSympathyPoints(newPlayer.userPoints, Const.SYMPATHIES_LIST[sympathy]),
                    3,
                );
                userScore = calcUserScore(userPoints, newPlayer.salary, newPlayer.userScoreMultiplier);
            }

            if (!player.captain_pointsChangedManually) {
                const sympathy = playersSympathy.getByPlayerIdAndMode(player.getId(), Const.CAPTAIN_MODE);
                captain_userPoints = NumberHelper.float(
                    calcSympathyPoints(newPlayer.captain_userPoints, Const.SYMPATHIES_LIST[sympathy]),
                    3,
                );
                captain_userScore = calcUserScore(
                    captain_userPoints,
                    newPlayer.captain_salary,
                    newPlayer.userScoreMultiplier,
                );
            }

            const {projOwn = {}, ...newPlayerProps} = newPlayer;

            if ('projOwn' in player) {
                player.projOwn.setValueFromPlayerPayload(newPlayer);
            }

            result[key] = {
                ...player,
                ...newPlayerProps,
                pointsChangedManually: player.pointsChangedManually,
                userPoints,
                userScore,
                exposureMax: player.exposureMax,
                exposureMin: player.exposureMin,
                captain_pointsChangedManually: player.captain_pointsChangedManually,
                captain_userPoints,
                captain_userScore,
                captain_exposureMax: player.captain_exposureMax,
                captain_exposureMin: player.captain_exposureMin,
                overall_exposureMax: player.overall_exposureMax,
            };
        });

        return {...result};
    }

    applySympathy({incomingValues: {players, playersSympathy}}) {
        const result = {};
        const indexedPlayers = players.all();

        Object.keys(indexedPlayers).map((key) => {
            const player = indexedPlayers[key];
            let userPoints = player.userPoints;
            let userScore = player.userScore;

            if (!player.pointsChangedManually) {
                const sympathy = playersSympathy.getByPlayerIdAndMode(player.getId());
                userPoints = NumberHelper.float(
                    calcSympathyPoints(player.userPoints, Const.SYMPATHIES_LIST[sympathy]),
                    3,
                );
                userScore = calcUserScore(userPoints, player.salary, player.userScoreMultiplier);
            }

            result[key] = {
                ...player,
                userPoints,
                userScore,
            };
        });

        return {...result};
    }

    applySympathy_ShowDown({incomingValues: {players, playersSympathy}}) {
        const result = {};
        const indexedPlayers = players.all();

        Object.keys(indexedPlayers).map((key) => {
            const player = indexedPlayers[key];
            let userPoints = player.userPoints;
            let userScore = player.userScore;
            let captain_userPoints = player.captain_userPoints;
            let captain_userScore = player.captain_userScore;

            if (!player.pointsChangedManually) {
                const sympathy = playersSympathy.getByPlayerIdAndMode(player.getId());
                userPoints = NumberHelper.float(
                    calcSympathyPoints(player.userPoints, Const.SYMPATHIES_LIST[sympathy]),
                    3,
                );
                userScore = calcUserScore(userPoints, player.salary, player.userScoreMultiplier);
            }

            if (!player.captain_pointsChangedManually) {
                const sympathy = playersSympathy.getByPlayerIdAndMode(player.getId(), Const.CAPTAIN_MODE);
                const captainSalary = player.getPropValueOfMode('salary', Const.CAPTAIN_MODE);
                captain_userPoints = NumberHelper.float(
                    calcSympathyPoints(player.captain_userPoints, Const.SYMPATHIES_LIST[sympathy]),
                    3,
                );
                captain_userScore = calcUserScore(captain_userPoints, captainSalary, player.userScoreMultiplier);
            }

            result[key] = {
                ...player,
                userPoints,
                userScore,
                captain_userPoints,
                captain_userScore,
            };
        });

        return {...result};
    }

    loadExtraPlayerDataFromStore_ShowDown({incomingValues: {dispatch, data}}) {
        if (data.hasOwnProperty('captain_userPoints')) {
            let captain_pointsChangedManually = false;
            if (typeof data['captain_pointsChangedManually'] !== 'undefined') {
                captain_pointsChangedManually = data['captain_pointsChangedManually'];
            }

            dispatch(
                Action.set('UserPoints', data.captain_userPoints, Const.CAPTAIN_MODE, captain_pointsChangedManually),
            );
        }

        if (data.hasOwnProperty('captain_userExposure')) {
            dispatch(Action.set('PlayersExposure', data.captain_userExposure));
        }
    }

    getExtraPlayerDataToStore_ShowDown({...rest}) {
        const {players} = Action.getState('playersData', {});

        if (!players.hasPlayers()) {
            return {};
        }

        const userPoints = [];
        const userExposure = [];
        const captain_pointsChangedManually = [];

        players.getAllAsArr().map((player) => {
            if (player.captain_adminPoints !== player.captain_userPoints) {
                userPoints.push({
                    playerId: player.playerId,
                    userPoints: player.captain_userPoints,
                    mode: Const.CAPTAIN_MODE,
                });
            }

            if (player.captain_exposureMax !== getDefaultExposure('max')) {
                userExposure.push({
                    playerId: player.playerId,
                    exposureMax: player.captain_exposureMax,
                    mode: Const.CAPTAIN_MODE,
                });
            }

            if (player.captain_exposureMin !== getDefaultExposure('min')) {
                userExposure.push({
                    playerId: player.playerId,
                    exposureMin: player.captain_exposureMin,
                    mode: Const.CAPTAIN_MODE,
                });
            }

            if (player.captain_pointsChangedManually) {
                captain_pointsChangedManually.push(player.playerId);
            }
        });

        return {
            captain_userPoints: userPoints,
            captain_userExposure: userExposure,
            captain_pointsChangedManually,
        };
    }

    getPlayerById({incomingValues: {id}}) {
        const {playersData} = Action.getState();
        const {preparedPlayers} = playersData;
        const players = preparedPlayers.groupedBy.playerId();

        return {...players[id]};
    }

    fillTeamsData(dispatch) {
        const {
            playersData: {players},
        } = Action.getState();

        const teams = {};
        players.getAllAsArr().map((player) => {
            teams[player.team] ??= {
                confirmed: 0,
                implied_run_totals: 0,
            };

            if (!teams[player.team].confirmed) {
                teams[player.team].confirmed = player.confirmed;
            }
            if (!teams[player.team].implied_run_totals) {
                teams[player.team].implied_run_totals = player.implied_run_totals;
            }
        });

        dispatch(Action.set('TeamDetails', teams));
    }
}
